import { Button } from '@cian/ui-kit';

import { type FC, useCallback, useState } from 'react';

import { useListingContext } from '@/microfrontends/listing/shared/context/ListingContext';

import { useSearchOffersDesktopQuery } from '@/packages/query-state/shared/store/search-offers/v3/search-offers-desktop/query';
import { boundActionsFactory } from '@/packages/redux-state/store';
import { usePageSelector } from '@/packages/redux-state/store/slices/jsonQuery/selectors/dumb';
import { extractUndefined } from '@/packages/utils/extractUndefined';

import { type PaginationItem } from '../types/pagination';

import { InfinityLoaderAreaContainer } from './InfinityLoaderAreaContainer';
import { PaginationContainer } from './PaginationContainer';

const useBoundActions = boundActionsFactory(reduxActions => ({
  setPage: reduxActions.jsonQuery.setPageWithUpdateQueryKey,
  incrementPage: reduxActions.jsonQuery.incrementPage,
}));

export const ListingPaginationContainer: FC = () => {
  const {
    listingProperties: { offersPerPage, totalOffers, maxPage },
  } = useListingContext();

  const [showInfinityLoader, setShowInfinityLoader] = useState(true);

  const { isFetchingNextPage, fetchNextPage, hasNextPage } = useSearchOffersDesktopQuery();

  const page = usePageSelector();

  const boundActions = useBoundActions();

  const handlePageClick = useCallback<(paginationItem: PaginationItem) => void>(
    async paginationItem => {
      boundActions.setPage(paginationItem.page);

      setShowInfinityLoader(true);
    },
    [boundActions],
  );

  const loadNextPage = useCallback(
    async (isInfinityLoaderShow: boolean) => {
      const { isFetched } = await fetchNextPage();

      /**
       * @description
       * Ждем, пока загрузятся новые офферы.
       * Потом с задержкой меняем состояние лоадера и страницу, чтобы офферы успели отрисоваться
       */
      if (isFetched) {
        setTimeout(() => {
          setShowInfinityLoader(isInfinityLoaderShow);

          boundActions.incrementPage();
        }, 100);
      }
    },
    [boundActions, fetchNextPage],
  );

  const handleLoadMoreClick = useCallback(() => loadNextPage(true), [loadNextPage]);

  const handleInfinityAreaReached = useCallback(() => loadNextPage(false), [loadNextPage]);

  const showInfinityPagination = Boolean(hasNextPage) && !isFetchingNextPage;

  return (
    <>
      <InfinityLoaderAreaContainer
        isActive={showInfinityPagination && showInfinityLoader}
        loading={isFetchingNextPage}
        onInViewport={handleInfinityAreaReached}
      />

      {showInfinityPagination && !showInfinityLoader && (
        <Button size="M" theme="fill_secondary" onClick={handleLoadMoreClick}>
          Показать ещё
        </Button>
      )}

      <PaginationContainer
        maxPage={extractUndefined(maxPage)}
        offersPerPage={offersPerPage}
        page={page}
        totalOffers={totalOffers}
        onPageClick={handlePageClick}
      />
    </>
  );
};
